/**
 * The energy slider (SPEC.md §8.3).
 *
 * A ceiling, not a priority filter: show tasks where `energy <= slider`. Anything marked
 * important or starred shows regardless — the slider must never hide something that
 * matters, and the label says so where it can be read.
 *
 * The value is committed on release rather than on every input event, so dragging from 1
 * to 5 writes one `energy_set` event instead of four.
 *
 * Every 1–5 a reader actually *sees* here is a die face (`Die`, the same drawn pips every
 * other energy badge in the app uses), not a numeral — the slider's own thumb still needs
 * real numbers for `min`/`max`/`value`/the `aria-value*` triad (a native range input has no
 * other way to be one), but nothing printed as text should say "3" when a die showing three
 * pips already means the same thing everywhere else energy appears.
 */

import { Die } from "./Die";

const LABELS: Record<number, string> = {
  1: "Running on empty",
  2: "Low",
  3: "Middling",
  4: "Good",
  5: "In flow",
};

export function EnergySlider({
  value,
  onChange,
  onCommit,
}: {
  value: number;
  onChange: (value: number) => void;
  onCommit: (value: number) => void | Promise<void>;
}) {
  return (
    <div className="energy">
      <div className="row spread">
        <label htmlFor="energy-slider" className="small muted">
          Show me work costing at most
        </label>
        <span className="reading">
          <Die value={value} size={16} title={`Energy ${value}`} /> · {LABELS[value]}
        </span>
      </div>

      <input
        id="energy-slider"
        type="range"
        min={1}
        max={5}
        step={1}
        value={value}
        aria-valuemin={1}
        aria-valuemax={5}
        aria-valuenow={value}
        aria-label="Energy ceiling"
        onChange={(e) => onChange(Number(e.target.value))}
        onMouseUp={(e) => void onCommit(Number((e.target as HTMLInputElement).value))}
        onTouchEnd={(e) => void onCommit(Number((e.target as HTMLInputElement).value))}
        onKeyUp={(e) => void onCommit(Number((e.target as HTMLInputElement).value))}
      />

      <div className="scale">
        <span>
          <Die value={1} size={13} title="Energy 1" /> · empty
        </span>
        <span>
          <Die value={5} size={13} title="Energy 5" /> · in flow
        </span>
      </div>

      <p className="muted small" style={{ margin: 0 }}>
        Expensive work is hidden, not gone. Starred and important tasks always show.
      </p>
    </div>
  );
}
